import { X } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import Tooltip from "@/common/components/tooltip/Tooltip";
import type {
  Notification,
  NotificationEventType,
} from "@/common/models/notification.model";

type Category =
  | "automation"
  | "manual"
  | "defect"
  | "comment"
  | "cycle"
  | "system";

export function getCategory(eventType: NotificationEventType): Category {
  if (eventType.startsWith("AUTOMATION_")) return "automation";
  if (eventType.startsWith("MANUAL_")) return "manual";
  if (eventType === "DEFECT_COMMENT_ADDED") return "comment";
  if (eventType.startsWith("DEFECT_")) return "defect";
  if (eventType.startsWith("CYCLE_")) return "cycle";
  return "system";
}

const CATEGORY_META: Record<
  Category,
  {
    chip: string;
  }
> = {
  automation: {
    chip: "border-info/25 bg-info/10 text-info",
  },
  manual: {
    chip: "border-border bg-muted text-ink-soft",
  },
  defect: {
    chip: "border-graph-failed/25 bg-graph-failed/10 text-graph-failed",
  },
  comment: {
    chip: "border-info/25 bg-info/10 text-info",
  },
  cycle: {
    chip: "border-border bg-muted text-ink-soft",
  },
  system: {
    chip: "border-border bg-muted text-ink-soft",
  },
};

const CATEGORY_CHIP_META: Record<
  Category,
  {
    label: string;
  }
> = {
  automation: {
    label: "Automation",
  },
  manual: {
    label: "Manual",
  },
  defect: {
    label: "Defect",
  },
  comment: {
    label: "Comment",
  },
  cycle: {
    label: "Cycle",
  },
  system: {
    label: "System",
  },
};

function getCategoryChipMeta(notification: Notification, category: Category) {
  if (notification.entityType === "TEST_PLAN_RESULT") {
    return { label: "Automation" };
  }

  if (notification.eventType === "AUTOMATION_RUN_ASSIGNED") {
    return { label: "Automation" };
  }

  if (notification.eventType === "MANUAL_RUN_ASSIGNED") {
    return { label: "Manual" };
  }

  if (notification.eventType === "CYCLE_CASE_RETEST") {
    return { label: "Retest" };
  }

  return CATEGORY_CHIP_META[category];
}

function extractEntityName(title: string): string {
  const idx = title.indexOf(": ");
  return idx !== -1 ? title.slice(idx + 2) : title;
}

function buildActionLine(n: Notification): React.ReactNode {
  const entityName = extractEntityName(n.title);
  const actor = n.actorName?.trim() || "System";

  const A = <span className="font-normal text-ink-strong">{actor}</span>;
  const E = <span className="font-normal text-ink-strong">{entityName}</span>;
  const commentActor = (
    <span className="font-normal text-ink-strong">{actor}</span>
  );
  const commentEntity = (
    <span className="font-normal text-ink-strong">{entityName}</span>
  );

  switch (n.eventType) {
    case "DEFECT_ASSIGNED":
      if (n.title.startsWith("Defect Review Requested")) {
        return (
          <>
            {A} added you as reviewer for {E}
          </>
        );
      }
      return (
        <>
          {A} assigned you to {E}
        </>
      );
    case "DEFECT_COMMENT_ADDED":
      return (
        <>
          {commentActor} mentioned you on {commentEntity}
        </>
      );
    case "DEFECT_STATUS_CHANGED":
      return (
        <>
          {A} changed status of {E}
        </>
      );
    case "DEFECT_RESOLVED":
      return (
        <>
          {A} resolved {E}
        </>
      );
    case "DEFECT_FIXED":
      return (
        <>
          {A} marked {E} as fixed —{" "}
          <span className="font-normal text-info">ready to retest</span>
        </>
      );
    case "DEFECT_CREATED":
      return (
        <>
          {A} created defect {E}
        </>
      );
    case "AUTOMATION_RUN_STARTED":
      return (
        <>
          {A} started automation run {E}
        </>
      );
    case "AUTOMATION_RUN_ASSIGNED":
      if (n.message.toLowerCase().includes("reviewer")) {
        return (
          <>
            {A} assigned you test case to review - {E}
          </>
        );
      }
      return (
        <>
          {A} assigned you automation test {E}
        </>
      );
    case "AUTOMATION_RUN_PASSED":
      return (
        <>
          Automation run{" "}
          <span className="font-normal text-graph-passed">passed</span> - {E}
        </>
      );
    case "AUTOMATION_RUN_FAILED":
      return (
        <>
          Automation run{" "}
          <span className="font-normal text-graph-failed">failed</span> - {E}
        </>
      );
    case "AUTOMATION_RUN_ABORTED":
      return (
        <>
          Automation run{" "}
          <span className="font-normal text-graph-aborted">stopped</span> - {E}
        </>
      );
    case "MANUAL_RUN_STARTED":
      return (
        <>
          {A} started manual run {E}
        </>
      );
    case "MANUAL_RUN_PASSED":
      return (
        <>
          Manual run{" "}
          <span className="font-normal text-graph-passed">passed</span> - {E}
        </>
      );
    case "MANUAL_RUN_FAILED":
      return (
        <>
          Manual run{" "}
          <span className="font-normal text-graph-failed">failed</span> - {E}
        </>
      );
    case "MANUAL_RUN_ASSIGNED":
      if (n.message.toLowerCase().includes("reviewer")) {
        return (
          <>
            {A} assigned you test case to review - {E}
          </>
        );
      }
      return (
        <>
          {A} assigned you manual run {E}
        </>
      );
    case "CYCLE_ASSIGNMENT":
      // The backend title is already the full user-facing sentence ("You have
      // been assigned testcases in cycle '<name>' from <actor email>"), so show
      // it verbatim instead of composing an actor/entity template.
      return <>{n.title}</>;
    case "CYCLE_CASE_RETEST":
      return (
        <>
          {A} marked{" "}
          <span className="font-normal text-ink-strong">
            {extractEntityName(n.title)}
          </span>{" "}
          for <span className="font-normal text-info">retest</span>
        </>
      );
    case "CYCLE_CASE_VERSION_UPDATED":
      return <>New version available for {E} — upgrade your Cycle?</>;
    default:
      return <>{n.title}</>;
  }
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatNotificationTimestamp(iso: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";

  const timeStr = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const startOfYesterday = startOfToday - 86_400_000;
  const startOfTomorrow = startOfToday + 86_400_000;
  const dateTime = date.getTime();

  if (dateTime >= startOfToday && dateTime < startOfTomorrow) {
    return `Today, ${timeStr}`;
  }
  if (dateTime >= startOfYesterday && dateTime < startOfToday) {
    return `Yesterday, ${timeStr}`;
  }

  const dateStr = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return `${dateStr}, ${timeStr}`;
}

type RunOutcome = "passed" | "failed" | "stopped" | "aborted";

function getRunOutcome(n: Notification): RunOutcome {
  if (n.eventType === "AUTOMATION_RUN_PASSED") return "passed";
  if (n.eventType === "AUTOMATION_RUN_FAILED") return "failed";
  const text = `${n.title} ${n.message}`.toLowerCase();
  if (text.includes("aborted")) return "aborted";
  return "stopped";
}

function getRunMeta(title: string) {
  const runMatch = title.match(/Run\s*#(\d+)/i);
  const runLabel = runMatch ? `Run #${runMatch[1]}` : null;
  const planName = title
    .replace(/\(Run\s*#\d+\)\s*$/i, "")
    .replace(
      /^Automation\s+run\s+(passed|failed|aborted|stopped)\s*[-:]\s*/i,
      "",
    )
    .replace(/^Manual\s+run\s+(passed|failed|aborted|stopped)\s*[-:]\s*/i, "")
    .trim();
  return {
    runLabel,
    planName: planName || extractEntityName(title),
  };
}

function shouldShowDetailMessage(
  notification: Notification,
  titleText: string,
): boolean {
  const detail = notification.message.trim();
  if (!detail) return false;

  const lowerDetail = detail.toLowerCase();
  if (lowerDetail === titleText.trim().toLowerCase()) return false;

  const repeatsAssignment =
    lowerDetail.startsWith("you have been assigned") ||
    lowerDetail.startsWith("you have been added as reviewer");
  const hasExtraSignal =
    lowerDetail.includes("priority:") || lowerDetail.includes("severity:");

  return !repeatsAssignment || hasExtraSignal;
}

function getDetailMessage(
  notification: Notification,
  titleText: string,
): string | null {
  if (!shouldShowDetailMessage(notification, titleText)) return null;

  const detail = notification.message.trim();
  const withoutSignals = detail
    .replace(/\s*[,.;]?\s*Priority:\s*[^,.;]+/gi, "")
    .replace(/\s*[,.;]?\s*Severity:\s*[^,.;]+/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  return withoutSignals || detail;
}

function getSignalChips(notification: Notification): string[] {
  const chips: string[] = [];
  const text = `${notification.title} ${notification.message}`;
  const seen = new Set<string>();

  const addChip = (value: string) => {
    const normalized = value.trim().replace(/\s+/g, " ");
    const key = normalized.toLowerCase();
    if (!normalized || seen.has(key)) return;
    seen.add(key);
    chips.push(normalized);
  };

  for (const match of text.matchAll(/\b(TC|CYC|DEF)-\d+\b/gi)) {
    addChip(match[0].toUpperCase());
  }

  for (const match of text.matchAll(/\b(Priority|Severity):\s*([^,.;]+)/gi)) {
    addChip(`${match[1]} ${match[2]}`);
  }

  return chips.slice(0, 3);
}

export function getNavigationPath(notification: Notification): string | null {
  const { entityType, entityId, commentId } = notification;
  if (!entityId) return null;
  const anchor = commentId ? `#comment-${commentId}` : "";
  switch (entityType) {
    case "DEFECT":
      return `/defects?id=${entityId}${anchor ? `&${anchor.slice(1)}` : ""}`;
    case "TEST_PLAN_RESULT":
      return commentId
        ? `/test-management/test-plans/${commentId}/results/${entityId}`
        : "/test-management/test-runs";
    case "TEST_PLAN":
      return `/test-management/test-plans/${entityId}`;
    case "DRY_TEST_PLAN":
      return `/testcases/${entityId}/testpage/manual`;
    case "CYCLE_CASE":
      // Cycle-case slot events (assignment / retest / version bump) are
      // "assigned-to-me" cycle work — land the user on My Queue, the universal
      // assigned-to-me workspace, where their full cycle assignment list lives.
      // (The old /test-management/cycles/:id route no longer exists.)
      return "/cycles/queue";
    case "TEST_CASE":
      return notification.eventType === "MANUAL_RUN_ASSIGNED"
        ? `/testcases/${entityId}/testpage/manual`
        : `/testcases/${entityId}/testpage/automation`;
    default:
      return null;
  }
}

interface NotificationItemProps {
  notification: Notification;
  onMarkRead: (id: number) => void;
  onDelete: (id: number) => void;
  canManage?: boolean;
  compact?: boolean;
}

const NotificationItem = ({
  notification: n,
  onMarkRead,
  onDelete,
  canManage = true,
  compact: _compact = false,
}: NotificationItemProps) => {
  const navigate = useNavigate();
  const category = getCategory(n.eventType);
  const meta = CATEGORY_META[category];
  const actorName = n.actorName?.trim() || "System";
  const navigationPath = getNavigationPath(n);
  const isRunResult = n.entityType === "TEST_PLAN_RESULT";
  const runOutcome = isRunResult ? getRunOutcome(n) : null;
  const runMeta = isRunResult ? getRunMeta(n.title) : null;
  const categoryChip = getCategoryChipMeta(n, category);
  const titleText =
    isRunResult && runMeta ? runMeta.planName : extractEntityName(n.title);
  const detailText = getDetailMessage(n, titleText);
  const signalChips = getSignalChips(n);

  const handleClick = () => {
    if (canManage && !n.read) onMarkRead(n.id);
    if (navigationPath) navigate(navigationPath);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(n.id);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
      className="group relative block cursor-pointer border-b border-border transition-colors outline-none hover:bg-muted/40 focus-visible:bg-muted/60"
    >
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 px-3.5 py-2.5 pr-8">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <span
              className={[
                "inline-flex h-4 shrink-0 items-center rounded border px-1.5 text-[10px] font-semibold uppercase tracking-wide",
                meta.chip,
              ].join(" ")}
            >
              {categoryChip.label}
            </span>
            <span className="min-w-0 truncate text-[11px] text-muted-foreground">
              - {actorName}
            </span>
          </div>

          <div className="mt-1 block truncate text-body-sm font-semibold leading-4 text-foreground">
            {isRunResult && runMeta && runOutcome ? (
              <>
                {runMeta.planName}
                {runMeta.runLabel && (
                  <span className="ml-1 text-[11px] font-normal text-muted-foreground">
                    • {runMeta.runLabel}
                  </span>
                )}
                <span
                  className={[
                    "ml-1 text-[11px] font-medium",
                    runOutcome === "passed"
                      ? "text-graph-passed"
                      : runOutcome === "failed"
                        ? "text-graph-failed"
                        : runOutcome === "aborted"
                          ? "text-graph-aborted"
                          : "text-graph-in-progress",
                  ].join(" ")}
                >
                  {runOutcome === "aborted"
                    ? "Aborted"
                    : runOutcome === "stopped"
                      ? "Stopped"
                      : runOutcome === "passed"
                        ? "Passed"
                        : "Failed"}
                </span>
              </>
            ) : (
              buildActionLine(n)
            )}
          </div>

          {detailText && (
            <div className="mt-1 line-clamp-1 text-[11px] leading-4 text-muted-foreground">
              {detailText}
            </div>
          )}

          {signalChips.length > 0 && (
            <div className="mt-1 flex min-w-0 flex-wrap gap-1">
              {signalChips.map((chip) => (
                <span
                  key={chip}
                  className="inline-flex h-4 items-center rounded bg-muted px-1.5 text-[10px] font-medium text-muted-foreground"
                >
                  {chip}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-start justify-end pt-0.5">
          <Tooltip
            content={formatDateTime(n.createdAt)}
            side="top"
            sideOffset={6}
          >
            <span className="text-[10px] font-medium text-muted-foreground tabular-nums whitespace-nowrap">
              {formatNotificationTimestamp(n.createdAt)}
            </span>
          </Tooltip>
        </div>
      </div>

      {!n.read && (
        <span
          className="absolute right-3.5 top-3.5 h-1.5 w-1.5 rounded-full bg-info"
          aria-label="Unread"
        />
      )}

      {canManage && (
        <button
          onClick={handleDelete}
          aria-label="Dismiss"
          className="absolute bottom-2 right-2 rounded p-1 text-muted-foreground opacity-0 transition-opacity duration-150 hover:bg-muted hover:text-foreground group-hover:opacity-100"
        >
          <X size={11} />
        </button>
      )}
    </div>
  );
};

export default NotificationItem;
